import { useState, useEffect, useCallback } from 'react';
import { adminApi } from '../lib/api';

function apiFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const token = localStorage.getItem('admin_token');
  return fetch(adminApi(url), { ...options, headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(options.headers as Record<string, string> || {}) } }).then(r => r);
}

export function UsersTab() {
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState<string>('');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [resetUser, setResetUser] = useState<any | null>(null);
  const [newPwd, setNewPwd] = useState('');
  const [pwdMsg, setPwdMsg] = useState('');
  const [pwdSaving, setPwdSaving] = useState(false);

  const load = useCallback(() => {
    setError('');
    apiFetch(`/api/admin/users?page=${page}&limit=20${search ? `&search=${encodeURIComponent(search)}` : ''}`)
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(setData)
      .catch(e => setError(e.message));
  }, [page, search]);

  useEffect(() => { load(); }, [load]);

  const updateUser = async (id: string, updates: any) => {
    await apiFetch(`/api/admin/users/${id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(updates) });
    load();
  };

  const handleResetPwd = async () => {
    if (!resetUser) return;
    setPwdSaving(true);
    setPwdMsg('');
    try {
      const r = await apiFetch(`/api/admin/users/${resetUser.id}/reset-password`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ password: newPwd }) });
      const d = await r.json();
      if (!r.ok) { setPwdMsg(d.error || '重置失败'); }
      else { setPwdMsg('密码已重置，用户需重新登录'); setNewPwd(''); setTimeout(() => { setResetUser(null); setPwdMsg(''); }, 1500); }
    } catch { setPwdMsg('网络错误'); }
    setPwdSaving(false);
  };

  if (error) return <div className="text-red-400 p-4">加载失败: {error} <button onClick={load} className="ml-2 text-cyan-400 underline">重试</button></div>;
  if (!data) return <div className="text-gray-500 p-4">加载中...</div>;

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">注册用户管理</h1>
      <div className="flex gap-4 mb-4">
        <input type="text" placeholder="搜索邮箱或用户名..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} className="flex-1 bg-gray-900 border border-gray-800 rounded px-3 py-2 text-sm" />
        <button onClick={load} className="px-4 py-2 bg-cyan-600 rounded text-sm">刷新</button>
      </div>
      <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-4 mb-4">
        <span className="text-gray-500 text-sm">总用户数: </span>
        <span className="text-cyan-400 font-bold">{data.total}</span>
      </div>
      <div className="bg-gray-900/50 border border-gray-800 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-800/50 text-gray-400">
            <tr>
              <th className="px-4 py-3 text-left">邮箱</th>
              <th className="px-4 py-3 text-left">用户名</th>
              <th className="px-4 py-3 text-left">套餐</th>
              <th className="px-4 py-3 text-left">状态</th>
              <th className="px-4 py-3 text-left">注册时间</th>
              <th className="px-4 py-3 text-left">最后登录</th>
              <th className="px-4 py-3 text-left">操作</th>
            </tr>
          </thead>
          <tbody>
            {(data.data || []).map((u: any) => (
              <tr key={u.id} className="border-t border-gray-800/50">
                <td className="px-4 py-3 text-gray-300">{u.email}</td>
                <td className="px-4 py-3 text-gray-300">{u.display_name}</td>
                <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded text-xs ${u.plan_type === 'enterprise' ? 'text-purple-400 bg-purple-500/10' : u.plan_type === 'professional' ? 'text-blue-400 bg-blue-500/10' : 'text-gray-400 bg-gray-500/10'}`}>{u.plan_type}</span></td>
                <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded text-xs ${u.status === 'active' ? 'text-emerald-400 bg-emerald-500/10' : 'text-red-400 bg-red-500/10'}`}>{u.status}</span></td>
                <td className="px-4 py-3 text-gray-500 text-xs">{u.created_at?.substring(0, 19)}</td>
                <td className="px-4 py-3 text-gray-500 text-xs">{u.last_login_at?.substring(0, 19) || '未登录'}</td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <select value={u.plan_type} onChange={e => updateUser(u.id, { plan_type: e.target.value })} className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs"><option value="free">free</option><option value="professional">professional</option><option value="enterprise">enterprise</option></select>
                  <button onClick={() => { setResetUser(u); setNewPwd(''); setPwdMsg(''); }} className="ml-2 text-xs text-amber-400 hover:text-amber-300">重置密码</button>
                  <button onClick={async () => { if (!confirm(`确认删除用户 ${u.email}？此操作不可恢复。`)) return; await apiFetch(`/api/admin/users/${u.id}`, { method: 'DELETE' }); load(); }} className="ml-2 text-xs text-red-400 hover:text-red-300">删除</button>
                </td>
              </tr>
            ))}
            {(!data.data || data.data.length === 0) && <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-600">暂无用户</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="flex justify-between items-center mt-4">
        <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-4 py-2 bg-gray-800 rounded text-sm disabled:opacity-30">上一页</button>
        <span className="text-gray-500 text-sm">第 {page} 页</span>
        <button onClick={() => setPage(p => p + 1)} disabled={data.data?.length < 20} className="px-4 py-2 bg-gray-800 rounded text-sm disabled:opacity-30">下一页</button>
      </div>
      {resetUser && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setResetUser(null)}>
          <div className="bg-gray-900 border border-gray-700 rounded-lg p-6 w-[400px]" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-white">重置密码</h2>
              <button onClick={() => setResetUser(null)} className="text-gray-500 hover:text-gray-300">✕</button>
            </div>
            <div className="text-xs text-gray-500 mb-4">用户: {resetUser.email}</div>
            <input type="password" value={newPwd} onChange={e => setNewPwd(e.target.value)} placeholder="新密码（至少8位，含字母和数字）" className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-sm text-white mb-3" />
            {pwdMsg && <div className={`text-sm mb-3 ${pwdMsg.includes('已重置') ? 'text-emerald-400' : 'text-red-400'}`}>{pwdMsg}</div>}
            <div className="flex justify-end gap-3">
              <button onClick={() => setResetUser(null)} className="px-4 py-2 bg-gray-800 text-gray-400 rounded text-sm hover:bg-gray-700">取消</button>
              <button onClick={handleResetPwd} disabled={pwdSaving || !newPwd} className="px-4 py-2 bg-amber-500/20 text-amber-400 rounded text-sm hover:bg-amber-500/30 disabled:opacity-50">{pwdSaving ? '重置中...' : '确认重置'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function OnlineTab() {
  const [data, setData] = useState<any>(null);
  const [stats, setStats] = useState<any>(null);
  const [error, setError] = useState<string>('');

  const load = useCallback(() => {
    setError('');
    apiFetch('/api/admin/users/online').then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); }).then(setData).catch(e => setError(e.message));
    apiFetch('/api/admin/users/stats').then(r => { if (!r.ok) return null; return r.json(); }).then(setStats).catch(() => {});
  }, []);

  useEffect(() => { load(); const i = setInterval(load, 10000); return () => clearInterval(i); }, [load]);

  if (error) return <div className="text-red-400 p-4">加载失败: {error} <button onClick={load} className="ml-2 text-cyan-400 underline">重试</button></div>;

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">客户端在线状态</h1>
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-4"><div className="text-gray-500 text-sm">总注册用户</div><div className="text-2xl font-bold text-cyan-400">{stats?.totalUsers ?? 0}</div></div>
        <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-4"><div className="text-gray-500 text-sm">在线客户端</div><div className="text-2xl font-bold text-emerald-400">{data?.onlineCount ?? 0}</div></div>
        <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-4"><div className="text-gray-500 text-sm">专业版</div><div className="text-2xl font-bold text-blue-400">{stats?.byPlan?.find((p: any) => p.plan_type === 'professional')?.count ?? 0}</div></div>
        <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-4"><div className="text-gray-500 text-sm">企业版</div><div className="text-2xl font-bold text-purple-400">{stats?.byPlan?.find((p: any) => p.plan_type === 'enterprise')?.count ?? 0}</div></div>
      </div>
      <h2 className="text-lg font-semibold mb-3">在线客户端列表 <span className="text-gray-500 text-sm font-normal">（每10秒刷新）</span></h2>
      <div className="bg-gray-900/50 border border-gray-800 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-800/50 text-gray-400">
            <tr>
              <th className="px-4 py-3 text-left">用户</th>
              <th className="px-4 py-3 text-left">实例ID</th>
              <th className="px-4 py-3 text-left">主机名</th>
              <th className="px-4 py-3 text-left">版本</th>
              <th className="px-4 py-3 text-left">模型数</th>
              <th className="px-4 py-3 text-left">24h请求</th>
              <th className="px-4 py-3 text-left">IP</th>
              <th className="px-4 py-3 text-left">最后心跳</th>
            </tr>
          </thead>
          <tbody>
            {(data?.onlineUsers || []).map((h: any, i: number) => (
              <tr key={i} className="border-t border-gray-800/50">
                <td className="px-4 py-3 text-gray-300">{h.user?.email || h.user?.display_name || <span className="text-gray-600">匿名</span>}</td>
                <td className="px-4 py-3 text-gray-500 text-xs">{h.instance_id}</td>
                <td className="px-4 py-3 text-gray-300">{h.host_name || '-'}</td>
                <td className="px-4 py-3 text-gray-500 text-xs">{h.version || '-'}</td>
                <td className="px-4 py-3 text-gray-300">{h.model_count}</td>
                <td className="px-4 py-3 text-gray-300">{h.request_count_24h}</td>
                <td className="px-4 py-3 text-gray-500 text-xs">{h.ip_address}</td>
                <td className="px-4 py-3 text-gray-500 text-xs">{h.created_at?.substring(0, 19)}</td>
              </tr>
            ))}
            {(!data?.onlineUsers || data.onlineUsers.length === 0) && <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-600">暂无在线客户端</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}